import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatNumber(n: number | null | undefined, decimals = 0): string {
  if (n == null) return '0';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);
}

export function formatCurrency(n: number | null | undefined, currency = 'USD'): string {
  if (n == null) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(n);
}

export function formatPercent(n: number | null | undefined, decimals = 2): string {
  if (n == null) return '0%';
  return `${n.toFixed(decimals)}%`;
}

export function formatDateTime(ts: number | string | null | undefined): string {
  if (!ts) return '-';
  const d = typeof ts === 'string' ? new Date(ts) : new Date(Number(ts));
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

export function formatRelativeTime(ts: number | null | undefined): string {
  if (!ts) return '-';
  // D14: clamp to 0 so clock-skew future timestamps show "0s ago" instead of "-3s ago"
  const diff = Math.max(0, Date.now() - ts);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  return `${days}d ago`;
}

export function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard) return navigator.clipboard.writeText(text);
  // Fallback
  const ta = document.createElement('textarea');
  ta.value = text;
  document.body.appendChild(ta);
  ta.select();
  document.execCommand('copy');
  document.body.removeChild(ta);
  return Promise.resolve();
}

export function getCountryFlag(iso2: string | null | undefined): string {
  if (!iso2 || iso2.length !== 2) return '🌐';
  const codePoints = iso2.toUpperCase().split('').map((c) => 127397 + c.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

/**
 * Returns a CDN URL for a country flag PNG at a specific width. Handles fallback for
 * unknown codes. Uses flagcdn.com which serves PNG at the supported widths below.
 * PNG is preferred over SVG because some browsers/extensions block raw SVG image loads.
 */
export function getCountryFlagUrl(iso2: string | null | undefined, widthPx = 24): string | null {
  if (!iso2 || iso2.length !== 2) return null;
  const supportedSizes = [16, 20, 24, 28, 32, 36, 40, 48, 56, 60, 64, 72, 80, 84, 96, 108];
  const px = supportedSizes.find((s) => s >= widthPx) ?? supportedSizes[supportedSizes.length - 1]!;
  const h = Math.round(px * 0.75);
  return `https://flagcdn.com/${px}x${h}/${iso2.toLowerCase()}.png`;
}

/** Device label for keys mobile/desktop/tablet/bot/etc. */
export function getDeviceLabel(device: string | null | undefined): string {
  if (!device) return 'Unknown';
  const d = device.toLowerCase();
  if (d.includes('mobile')) return 'Mobile';
  if (d.includes('tablet')) return 'Tablet';
  if (d.includes('desktop')) return 'Desktop';
  if (d.includes('bot')) return 'Bot';
  return device;
}
